import { useEffect, useMemo, useState } from "react";
import { Bell, Mail, Save, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const AlertsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    weekly_digest: true,
    niches: [],
    keywords: [],
    min_score: 0,
  });
  const [nichesText, setNichesText] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [notifications, setNotifications] = useState([]);

  const parseCommaList = (value) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prefsRes, notificationsRes] = await Promise.all([
        apiClient.get("/alerts/preferences"),
        apiClient.get("/alerts/notifications?limit=30"),
      ]);
      const prefs = prefsRes.data || {};
      setPreferences({
        email_notifications: !!prefs.email_notifications,
        weekly_digest: !!prefs.weekly_digest,
        niches: Array.isArray(prefs.niches) ? prefs.niches : [],
        keywords: Array.isArray(prefs.keywords) ? prefs.keywords : [],
        min_score: Number(prefs.min_score || 0),
      });
      setNichesText((prefs.niches || []).join(", "));
      setKeywordsText((prefs.keywords || []).join(", "));
      setNotifications(notificationsRes.data?.notifications || []);
    } catch (error) {
      console.error("Failed to load alerts:", error);
      toast.error("Failed to load alert settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const digestNotifications = useMemo(
    () => notifications.filter((notification) => notification.type === "weekly_digest"),
    [notifications]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...preferences,
        niches: parseCommaList(nichesText),
        keywords: parseCommaList(keywordsText),
        min_score: Number(preferences.min_score || 0),
      };
      const response = await apiClient.put("/alerts/preferences", payload);
      setPreferences({
        email_notifications: !!response.data.email_notifications,
        weekly_digest: !!response.data.weekly_digest,
        niches: response.data.niches || [],
        keywords: response.data.keywords || [],
        min_score: Number(response.data.min_score || 0),
      });
      setNichesText((response.data.niches || []).join(", "));
      setKeywordsText((response.data.keywords || []).join(", "));
      toast.success("Alert preferences saved");
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast.error("Could not save alert preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleTestDigest = async () => {
    setSendingTest(true);
    try {
      const response = await apiClient.post("/alerts/test-digest");
      toast.success(response.data?.message || "Test digest generated");
      await loadData();
    } catch (error) {
      console.error("Failed to trigger test digest:", error);
      toast.error(error.response?.data?.detail || "Failed to generate test digest");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-5 md:p-8 lg:p-12">
        <div className="w-10 h-10 border-4 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-5 md:p-8 lg:p-12 pb-[calc(2rem+env(safe-area-inset-bottom))] md:pb-8 lg:pb-12 max-w-5xl">
      <div className="mb-6 md:mb-8">
        <h1
          className="text-2xl md:text-4xl font-bold tracking-tight text-white mb-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Alerts & Weekly Digest
        </h1>
        <p className="text-zinc-400">
          Personalize opportunity alerts and receive weekly digests tailored to your filters.
        </p>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8 mb-5 md:mb-6">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <Bell className="w-5 h-5 text-zinc-400" />
          <h2 className="text-lg font-semibold text-white">Digest Preferences</h2>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-white/[0.02]">
            <div className="min-w-0 pr-2">
              <h3 className="font-medium text-white">Email Notifications</h3>
              <p className="text-sm text-zinc-500">Receive digest updates by email (simulated in logs for now)</p>
            </div>
            <Switch
              checked={preferences.email_notifications}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({ ...prev, email_notifications: checked }))
              }
              data-testid="alerts-email-switch"
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-white/[0.02]">
            <div className="min-w-0 pr-2">
              <h3 className="font-medium text-white">Weekly Digest</h3>
              <p className="text-sm text-zinc-500">Generate a weekly personalized opportunity digest</p>
            </div>
            <Switch
              checked={preferences.weekly_digest}
              onCheckedChange={(checked) =>
                setPreferences((prev) => ({ ...prev, weekly_digest: checked }))
              }
              data-testid="alerts-weekly-switch"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-5">
          <div>
            <Label className="text-zinc-300 mb-2 block">Niches (comma-separated)</Label>
            <Input
              value={nichesText}
              onChange={(e) => setNichesText(e.target.value)}
              placeholder="AI, Fintech, Developer Tools"
              className="bg-[#161616] border-[#1F1F1F] rounded-xl text-white"
              data-testid="alerts-niches-input"
            />
          </div>
          <div>
            <Label className="text-zinc-300 mb-2 block">Keywords (comma-separated)</Label>
            <Input
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder="chrome extension, automation, ecommerce"
              className="bg-[#161616] border-[#1F1F1F] rounded-xl text-white"
              data-testid="alerts-keywords-input"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Button
            className="w-full sm:w-auto bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl"
            onClick={handleSave}
            disabled={saving}
            data-testid="alerts-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Preferences"}
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-xl"
            onClick={handleTestDigest}
            disabled={sendingTest}
            data-testid="alerts-test-digest-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sendingTest ? "Generating..." : "Send Test Digest"}
          </Button>
        </div>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <Mail className="w-5 h-5 text-zinc-400" />
          <h2 className="text-lg font-semibold text-white">Digest History</h2>
        </div>

        {digestNotifications.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
            No digests yet. Save preferences and send a test digest.
          </div>
        ) : (
          <div className="space-y-4">
            {digestNotifications.map((notification) => (
              <div
                key={notification.notification_id}
                className="rounded-2xl border border-[#1F1F1F] bg-white/[0.02] p-5"
                data-testid={`digest-item-${notification.notification_id}`}
              >
                <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <h3 className="text-white font-semibold">{notification.title}</h3>
                  <span className="text-xs text-zinc-500">
                    {new Date(notification.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-zinc-400 mb-4">{notification.message}</p>
                <div className="space-y-2">
                  {(notification.digest_items || []).map((item) => (
                    <div
                      key={`${notification.notification_id}-${item.opportunity_id}`}
                      className="rounded-xl border border-white/[0.06] bg-black/20 p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-white font-medium">{item.name}</p>
                        <span className="text-xs text-[#A78BFA] font-semibold">
                          Score {Math.round(item.opportunity_score || 0)}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {item.niche} · {item.trend_status}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;
